import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import {
  AutomationContext,
  AutomationRuleGroupBoolean,
  automationRuleGroupBooleanOptions,
  AutomationRuleType,
  automationRuleTypeByContext,
  IAutomationRule,
  IAutomationRuleGroup,
} from '@kinet-open-network/shared/data-access/model';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { AutomationConditionRuleContainerComponent } from './automation-condition-rule-container.component';
import { AutomationConditionService } from './automation-condition.service';

@Component({
  selector: 'kon-automation-condition-rule-group',
  imports: [
    AutomationConditionRuleContainerComponent,
    ButtonModule,
    MenuModule,
    DividerModule,
  ],
  template: `
    <div class="flex items-center gap-2 pl-2">
      <button
        type="button"
        (click)="toggleCollapsed()"
        class="flex items-center gap-2 grow"
      >
        <i
          [class]="collapsed() ? 'pi pi-chevron-right' : 'pi pi-chevron-down'"
        ></i>
        <span class="font-medium text-sm">Skupina podmienok</span>
      </button>

      <div>
        <button
          pButton
          type="button"
          icon="pi pi-ellipsis-v"
          size="small"
          class="p-button-text"
          (click)="menu.toggle($event)"
        ></button>
        <p-menu #menu [popup]="true" [model]="menuItems()" appendTo="body">
        </p-menu>
      </div>
    </div>

    @if (!collapsed()) {
      @for (child of children(); track child.id) {
        @if (child.type === 'rule') {
          <kon-automation-condition-rule-container
            [enabledRules]="enabledRules()"
            [rule]="child"
            (ruleChange)="handleChildChange($index, $event)"
            (ruleRemove)="handleRemoveChild($index)"
          />
        } @else if (child.type === 'group') {
          <kon-automation-condition-rule-group
            [context]="context()"
            [group]="child"
            (groupChange)="handleChildChange($index, $event)"
            (removeGroup)="handleRemoveChild($index)"
          />
        }

        @if (!$last) {
          <span class="text-gray-400 text-sm pl-2">{{
            booleanOptions.get(boolean())?.label
          }}</span>
        }
      } @empty {
        <p class="text-gray-400 pl-2">Žiadne podmienky</p>
      }

      <p-divider class="my-1" />

      <div class="flex items-center gap-2">
        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Pridať podmienku"
          size="small"
          [outlined]="true"
          (click)="handleAddRule()"
        ></button>

        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Pridať skupinu"
          size="small"
          [outlined]="true"
          (click)="handleAddGroup()"
        ></button>
      </div>
    }
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationConditionRuleGroupComponent {
  readonly #conditionService = inject(AutomationConditionService);

  readonly group = model<IAutomationRuleGroup>(
    this.#conditionService.createRuleGroup(),
  );
  readonly canDelete = input(true);
  readonly context = input<AutomationContext>();
  readonly enabledRules = computed<AutomationRuleType[]>(() => {
    const ctx = this.context();

    if (!ctx) return [];

    return automationRuleTypeByContext[ctx];
  });
  readonly children = computed(() => this.group().children);
  readonly boolean = computed(() => this.group().boolean);
  readonly collapsed = computed(() => this.group().collapsed);
  readonly nextBoolean = computed(() => {
    if (this.boolean() === AutomationRuleGroupBoolean.And) {
      return AutomationRuleGroupBoolean.Or;
    }

    return AutomationRuleGroupBoolean.And;
  });
  readonly removeGroup = output<void>();

  readonly booleanOptions = automationRuleGroupBooleanOptions;

  readonly menuItems = computed<MenuItem[]>(() => [
    {
      label: 'Zmaž skupinu',
      icon: 'pi pi-times',
      command: () => this.handleRemoveGroup(),
      visible: this.canDelete(),
    },
    {
      label: `Zmeniť operátor na "${this.booleanOptions.get(this.nextBoolean())?.label.toLowerCase()}"`,
      icon: 'pi pi-refresh',
      command: () => this.handleChangeBoolean(),
    },
    {
      label: this.collapsed() ? 'Rozbaliť skupinu' : 'Zbaliť skupinu',
      icon: this.collapsed() ? 'pi pi-plus' : 'pi pi-minus',
      command: () => this.toggleCollapsed(),
    },
  ]);

  @HostBinding('class')
  get hostClass() {
    return 'flex flex-col gap-2 border border-gray-200 p-2 rounded-md';
  }

  handleChildChange(
    index: number,
    rule: Partial<IAutomationRule | IAutomationRuleGroup>,
  ) {
    const children = this.children();
    children[index] = { ...rule } as IAutomationRule | IAutomationRuleGroup;

    this.group.set({ ...this.group(), children: [...children] });
  }

  handleRemoveChild(index: number) {
    const children = this.children();
    children.splice(index, 1);

    this.group.set({ ...this.group(), children: [...children] });
  }

  handleAddRule() {
    const children = this.children();
    const enabledRules = this.enabledRules();
    const newRule = {
      ...this.#conditionService.createRule(
        enabledRules?.length ? enabledRules[0] : undefined,
      ),
    } as IAutomationRule;

    children.push(newRule);

    this.group.set({ ...this.group(), children });
  }

  handleAddGroup() {
    const children = this.children();
    children.push(this.#conditionService.createRuleGroup());

    this.group.set({ ...this.group(), children });
  }

  handleRemoveGroup() {
    this.removeGroup.emit();
  }

  handleChangeBoolean() {
    const boolean = this.nextBoolean();
    this.group.set({ ...this.group(), boolean });
  }

  toggleCollapsed() {
    this.group.update((group) => ({ ...group, collapsed: !group.collapsed }));
  }
}
